export function formatAmount(kopeks: number | null | undefined): string {
  if (kopeks == null) return "—";
  return (kopeks / 100).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const RELATIVE_TIME_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 60 * 60 * 24 * 365],
  ["month", 60 * 60 * 24 * 30],
  ["day", 60 * 60 * 24],
  ["hour", 60 * 60],
  ["minute", 60],
];
const relativeTimeFormatter = new Intl.RelativeTimeFormat("en", {numeric: "always"});

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  for (const [unit, secondsInUnit] of RELATIVE_TIME_UNITS) {
    if (Math.abs(diffSeconds) >= secondsInUnit) {
      return relativeTimeFormatter.format(Math.round(diffSeconds / secondsInUnit), unit);
    }
  }
  return relativeTimeFormatter.format(diffSeconds, "second");
}

// The server sends the age it measured itself, so the browser clock never
// enters the calculation: a laptop running a couple of minutes ahead of the
// service used to turn a fresh quote into "2 minutes ago".
export function formatAgeSeconds(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  const age = Math.max(0, Math.round(seconds));
  if (age < 60) return `${age} s ago`;
  return relativeTimeFormatter.format(-Math.round(age / 60), "minute");
}

export function statusClass(status: string | null | undefined): string {
  if (!status) return "";
  const s = status.toLowerCase();
  if (["success", "completed", "done", "paid"].includes(s))
    return "status-success";
  if (["pending", "processing", "waiting"].includes(s))
    return "status-pending";
  if (["appeal", "dispute", "disputed"].includes(s))
    return "status-appeal";
  if (["failed", "error", "declined", "cancelled"].includes(s))
    return "status-failed";
  return "status-default";
}

export function maskCard(card: string | null | undefined): string {
  if (!card || card.length < 8) return card || "—";
  return `${card.slice(0, 4)} •••• •••• ${card.slice(-4)}`;
}

/** Номер телефона заявки, лежащей в пуле: видны только последние 4 цифры.
 *  Это защита от дурака, а не сокрытие данных — трейдер, который взял заявку,
 *  видит номер целиком (там показывается clientPhone как есть). До взятия
 *  показывать номер полностью незачем: скопировать и отправить перевод по
 *  чужой заявке проще, чем кажется, а последних четырёх цифр хватает, чтобы
 *  отличить одну заявку от другой. Карта в пуле закрывается так же (maskCard),
 *  номер до сих пор оставался открытым.
 *
 *  Скрывается всё, кроме хвоста: у номера нет части вроде BIN, по которой
 *  что-то решают, поэтому начало не показываем вовсе. Номер короче пяти цифр
 *  закрывается целиком — открывать там нечего. */
export function maskPhone(phone: string | null | undefined): string {
  const digits = (phone ?? "").replace(/\D/g, "");
  if (!digits) return "—";
  if (digits.length <= 4) return "••••";
  return `•••• ${digits.slice(-4)}`;
}

/** Реквизиты клиента так, как их видит трейдер: `inPool` — заявка ещё лежит в
 *  пуле и не принадлежит никому, значит и карта, и номер закрыты. Взял заявку —
 *  видит всё.
 *
 *  Решение вынесено из разметки отдельной функцией не ради красоты: в карточке
 *  заявки реквизиты показываются в двух местах — в свёрнутой строке и в
 *  раскрытых деталях, — и одно и то же условие было написано там дважды.
 *  Второе место живёт за useState, серверный рендер до него не добирается, и
 *  проверить его тестом было нечем. Теперь проверяется решение, а не разметка. */
export function traderRequisites(
  requisites: {clientCard?: string | null; clientPhone?: string | null},
  inPool: boolean,
): {card: string; phone: string} {
  return inPool
    ? {card: maskCard(requisites.clientCard), phone: maskPhone(requisites.clientPhone)}
    : {card: formatCard(requisites.clientCard), phone: requisites.clientPhone || "—"};
}

/** Formats a client card / account for the trader who has taken the order into
 *  work. Cards are stored as 16 digits without separators (see backend
 *  normalizeCard); group them into blocks of 4 for readability. Non-standard
 *  values (empty, other length) are returned unchanged. Unlike maskCard, this
 *  shows the full number — used only for orders the trader already owns. */
export function formatCard(card: string | null | undefined): string {
  if (!card) return "—";
  if (/^\d{16}$/.test(card)) {
    return card.replace(/(\d{4})(?=\d)/g, "$1 ");
  }
  return card;
}

/** Ставка со знаком для показа: "2" → "+2%", "-1.5" → "−1.5%", "0" → "+0%".
 *  Минус заменяем на типографский, плюс дописываем сами — знак ставки определяет
 *  направление спреда, и он обязан быть виден. Строка с бэка не парсится в
 *  число: ставка — часть денежной формулы. */
export function formatRateMarkup(percent: string | null | undefined): string {
  if (percent == null) return "—";
  const value = percent.trim();
  if (value === "") return "—";
  if (value.startsWith("-")) return `−${value.slice(1)}%`;
  if (value.startsWith("+")) return `+${value.slice(1)}%`;
  return `+${value}%`;
}

const RATE_SOURCE_LABELS: Record<string, string> = {
  rapira: "Rapira",
};

/** Расшифровка зафиксированного курса заявки: "82.97 (Rapira 81.34 +2%)".
 *  Возвращает null, если снапшота нет — у обычных заявок курс вводит трейдер. */
export function formatFixedRate(payment: {
  rateFixed?: string | null;
  rateSource?: string | null;
  rateBase?: string | null;
  rateMarkupPercent?: string | null;
}): string | null {
  if (!payment.rateFixed) return null;
  if (!payment.rateSource || !payment.rateBase || payment.rateMarkupPercent == null) return payment.rateFixed;
  const sourceLabel = RATE_SOURCE_LABELS[payment.rateSource] ?? payment.rateSource;
  return `${payment.rateFixed} (${sourceLabel} ${truncateAmount(payment.rateBase)} ${formatRateMarkup(payment.rateMarkupPercent)})`;
}

/** Ключ 2FA группами по 4 символа: так его легче вводить вручную. */
export function groupSecondFactorSecret(secret: string): string {
  return secret.replace(/(.{4})/g, "$1 ").trim();
}

/** Truncates a backend-formatted decimal string to 2 fraction digits for
 *  display. String-only: money strings are never parsed into numbers. */
export function truncateAmount(amount: string): string {
  const dotIndex = amount.indexOf(".");
  if (dotIndex === -1) return amount;
  const fractionLength = amount.length - dotIndex - 1;
  if (fractionLength <= 2) return amount;
  return amount.slice(0, dotIndex + 3);
}
